/**
 * Three-step onboarding wizard. Mirrors the Android `OnboardingActivity`
 * verbatim: Welcome → Privacy filter model → All set summary. The "folder
 * picker" middle step from the Android version is replaced with a model
 * picker because the web app has no SAF equivalent — instead, the user
 * picks a variant and either downloads it in-browser (transformers.js)
 * or imports already-fetched files from disk.
 */

import { VARIANTS, humanBytes, isInstalled, type Variant } from "./models.js";
import { loadPii, type PiiProgress } from "./pii.js";
import { loadSettings, saveSettings, type SettingsValues, type VariantId } from "./settings.js";

export interface OnboardingResult {
  variantId: VariantId;
}

const ONBOARDED_FLAG = "redact_onboarded_v1";

export function isOnboardingCompleted(): boolean {
  try {
    return localStorage.getItem(ONBOARDED_FLAG) === "1";
  } catch {
    return false;
  }
}

export function markOnboardingCompleted(): void {
  try {
    localStorage.setItem(ONBOARDED_FLAG, "1");
  } catch {
    /* ignore */
  }
}

export interface RunOnboardingOptions {
  /** Mount point. The wizard owns the children of this element. */
  root: HTMLElement;
  /** True when triggered from a "model went missing" recovery state, not
   *  the first launch. The welcome step is skipped and step 2 surfaces a
   *  banner explaining what happened. */
  recovery?: boolean;
}

/**
 * Pre-step explainer. Not numbered — it's the first thing the user sees,
 * before the legal waiver and the wizard. Resolves on Continue.
 */
export function runWelcome(root: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    root.innerHTML = "";
    const shell = document.createElement("div");
    shell.className = "onb-shell";

    // Blank header so vertical rhythm matches the wizard pages — no
    // step counter on the explainer.
    const header = document.createElement("header");
    header.className = "onb-header onb-header-blank";
    header.innerHTML = `<span class="onb-back" style="visibility:hidden">←</span><span></span><span class="onb-back" style="visibility:hidden">←</span>`;
    shell.appendChild(header);

    const flipper = document.createElement("div");
    flipper.className = "onb-flipper";
    flipper.appendChild(renderWelcome());
    shell.appendChild(flipper);

    const cta = document.createElement("div");
    cta.className = "onb-cta-bar";
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "pill pill-primary";
    btn.textContent = "Get started";
    btn.addEventListener("click", () => resolve());
    cta.appendChild(btn);
    shell.appendChild(cta);

    root.appendChild(shell);
  });
}

/**
 * The wizard proper — runs after the welcome explainer + the legal
 * waiver. Two steps: pick a privacy filter (rendered as Step 2 of 3)
 * and the all-set summary (Step 3 of 3). Step 1 of 3 is owned by the
 * legal waiver; the wizard's eyebrow numbering is offset by +1 so the
 * user sees "Step 2 of 3" then "Step 3 of 3".
 */
export async function runOnboarding(opts: RunOnboardingOptions): Promise<OnboardingResult> {
  return new Promise((resolve) => {
    const settings = loadSettings();
    // Two steps: 0 = picker, 1 = summary. The header offsets the
    // eyebrow by +2 so they read as "Step 2 of 3" / "Step 3 of 3".
    let step = 0;
    let pickedVariant: VariantId = settings.variantId;

    const render = () => {
      opts.root.innerHTML = "";
      const shell = document.createElement("div");
      shell.className = "onb-shell";

      shell.appendChild(renderHeader(step + 2, () => {
        if (step > 0) {
          step--;
          render();
        }
      }));

      const flipper = document.createElement("div");
      flipper.className = "onb-flipper";
      if (step === 0) {
        flipper.appendChild(renderPicker({
          recovery: !!opts.recovery,
          selected: pickedVariant,
          onSelect: (id) => {
            pickedVariant = id;
            settings.variantId = id;
            saveSettings(settings);
            render();
          },
        }));
      } else {
        flipper.appendChild(renderSummary(pickedVariant));
      }
      shell.appendChild(flipper);

      shell.appendChild(renderCta(step + 2, pickedVariant, async (advance) => {
        if (advance === "next") {
          if (step < 1) {
            step++;
            render();
          } else {
            markOnboardingCompleted();
            const final: SettingsValues = { ...settings, variantId: pickedVariant };
            saveSettings(final);
            resolve({ variantId: pickedVariant });
          }
        }
      }));

      opts.root.appendChild(shell);
    };
    render();
  });
}

/** `stepNum` is the human-readable step (1..3); the back arrow is hidden
 *  on step 1 because the legal waiver owns it and the user can't go
 *  back to legal once they've ticked through it. */
function renderHeader(stepNum: number, onBack: () => void): HTMLElement {
  const header = document.createElement("header");
  header.className = "onb-header";
  const back = document.createElement("button");
  back.type = "button";
  back.className = "onb-back";
  back.setAttribute("aria-label", "Back");
  back.textContent = "←";
  // Hide back when we're on the first wizard page (legal owns "step 1");
  // pages 2 and 3 can rewind within the wizard.
  if (stepNum <= 2) back.style.visibility = "hidden";
  back.addEventListener("click", onBack);
  header.appendChild(back);

  const eyebrow = document.createElement("span");
  eyebrow.className = "onb-eyebrow";
  eyebrow.textContent = `Step ${stepNum} of 3`;
  header.appendChild(eyebrow);

  const spacer = document.createElement("span");
  spacer.className = "onb-back";
  spacer.style.visibility = "hidden";
  spacer.textContent = "←";
  header.appendChild(spacer);

  return header;
}

function renderWelcome(): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "onb-step";
  wrap.innerHTML = `
    <h1 class="serif onb-title">Share images,<br/>not what's in them.</h1>
    <p class="onb-body">Redact runs OCR on your image, then a small open-source model called Privacy Filter flags names, addresses, account numbers, and other sensitive spans.</p>
    <p class="onb-body">You see exactly what would be hidden before you share. Add or edit boxes by hand if anything looks off.</p>
    <ul class="onb-bullets">
      <li>Every model runs locally in your browser.</li>
      <li>No account, no telemetry, no upload.</li>
      <li>You decide what gets covered before sharing.</li>
    </ul>
  `;
  return wrap;
}

interface PickerArgs {
  recovery: boolean;
  selected: VariantId;
  onSelect: (id: VariantId) => void;
}

function renderPicker(args: PickerArgs): HTMLElement {
  const wrap = document.createElement("section");
  wrap.className = "onb-step";

  if (args.recovery) {
    const banner = document.createElement("p");
    banner.className = "onb-banner";
    banner.textContent =
      "The model files could not be detected. Maybe browser storage was cleared. Pick the variant you want to re-install below.";
    wrap.appendChild(banner);
  }

  const title = document.createElement("h2");
  title.className = "serif onb-title-sm";
  title.textContent = "Pick the privacy filter";
  wrap.appendChild(title);

  const body = document.createElement("p");
  body.className = "onb-body";
  body.textContent =
    "All four variants come from openai/privacy-filter on Hugging Face. Smaller variants download faster and use less memory; larger ones catch more.";
  wrap.appendChild(body);

  const list = document.createElement("div");
  list.className = "onb-selector";
  for (const v of VARIANTS) {
    list.appendChild(renderVariantRow(v, args.selected === v.id, () => args.onSelect(v.id)));
  }
  wrap.appendChild(list);

  const links = document.createElement("p");
  links.className = "onb-hint";
  links.innerHTML = `Prefer to download manually? Files live on <a href="https://huggingface.co/openai/privacy-filter/tree/main/onnx" target="_blank" rel="noopener">huggingface.co</a> — they'll be picked up automatically if your browser already cached them.`;
  wrap.appendChild(links);

  return wrap;
}

function renderVariantRow(v: Variant, selected: boolean, onClick: () => void): HTMLElement {
  const row = document.createElement("button");
  row.type = "button";
  row.className = "onb-variant" + (selected ? " is-selected" : "");

  const head = document.createElement("div");
  head.className = "onb-variant-head";
  const name = document.createElement("span");
  name.className = "onb-variant-name serif";
  name.textContent = v.displayName;
  head.appendChild(name);
  if (v.recommended) {
    const badge = document.createElement("span");
    badge.className = "onb-pill";
    badge.textContent = "Recommended";
    head.appendChild(badge);
  }
  if (isInstalled(v.id)) {
    const badge = document.createElement("span");
    badge.className = "onb-pill onb-pill-installed";
    badge.textContent = "Installed";
    head.appendChild(badge);
  }
  row.appendChild(head);

  const tag = document.createElement("p");
  tag.className = "onb-variant-tag";
  tag.textContent = v.tagline;
  row.appendChild(tag);

  const meta = document.createElement("p");
  meta.className = "onb-variant-meta";
  meta.textContent = `${humanBytes(v.totalBytes)} · ${v.files.length} files`;
  row.appendChild(meta);

  row.addEventListener("click", onClick);
  return row;
}

function renderSummary(variantId: VariantId): HTMLElement {
  const v = VARIANTS.find((x) => x.id === variantId)!;
  const wrap = document.createElement("section");
  wrap.className = "onb-step";

  const chip = document.createElement("div");
  chip.className = "onb-checkchip";
  chip.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  wrap.appendChild(chip);

  const title = document.createElement("h1");
  title.className = "serif onb-title";
  title.textContent = "You're set up.";
  wrap.appendChild(title);

  const body = document.createElement("p");
  body.className = "onb-body";
  body.textContent =
    "Drop a screenshot below — Redact will OCR it locally, then ask the privacy filter what to cover. Everything stays in this tab.";
  wrap.appendChild(body);

  const summary = document.createElement("div");
  summary.className = "onb-summary";
  summary.appendChild(summaryRow("Privacy filter", `${v.displayName} · ${humanBytes(v.totalBytes)}`));
  summary.appendChild(summaryRow("Recogniser", "Tesseract.js · English bundled"));
  summary.appendChild(summaryRow("Engine", isWebGPUAvailable() ? "WebGPU" : "CPU with WASM"));
  wrap.appendChild(summary);

  return wrap;
}

function summaryRow(label: string, value: string): HTMLElement {
  const row = document.createElement("div");
  row.className = "onb-summary-row";
  const k = document.createElement("span");
  k.className = "onb-summary-label";
  k.textContent = label.toUpperCase();
  const sep = document.createElement("span");
  sep.className = "onb-summary-spacer";
  const val = document.createElement("span");
  val.className = "onb-summary-value serif";
  val.textContent = value;
  row.appendChild(k);
  row.appendChild(sep);
  row.appendChild(val);
  return row;
}

/** `stepNum` is the human-readable step (2 = picker, 3 = summary) since
 *  step 1 (legal) is owned by the waiver. */
function renderCta(
  stepNum: number,
  variantId: VariantId,
  onAdvance: (kind: "next") => void,
): HTMLElement {
  const bar = document.createElement("div");
  bar.className = "onb-cta-bar";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "pill pill-primary";

  if (stepNum === 2) {
    btn.textContent = isInstalled(variantId) ? "Continue" : "Continue (downloads on first use)";
  } else {
    btn.textContent = "Open Redact";
  }
  btn.addEventListener("click", () => onAdvance("next"));
  bar.appendChild(btn);
  return bar;
}

function isWebGPUAvailable(): boolean {
  return Boolean((navigator as unknown as { gpu?: unknown }).gpu);
}

/* -------------------------------------------------------------------------- */
/* Inline progress overlay used during the very-first model warm-up.          */
/* -------------------------------------------------------------------------- */

export interface WarmupHandle {
  /** Resolves once the model is fully loaded (or rejects). */
  done: Promise<void>;
}

export function warmupVariant(
  root: HTMLElement,
  id: VariantId,
): WarmupHandle {
  root.innerHTML = "";
  const shell = document.createElement("div");
  shell.className = "warmup-shell";
  shell.innerHTML = `
    <div class="warmup-card">
      <h2 class="serif warmup-title">Preparing the privacy filter…</h2>
      <p class="warmup-body" id="warmup-stage">Connecting…</p>
      <div class="warmup-progress"><div id="warmup-bar"></div></div>
      <p class="warmup-hint">First-time download from huggingface.co. Next time it will be faster.</p>
    </div>
  `;
  root.appendChild(shell);
  const bar = shell.querySelector<HTMLDivElement>("#warmup-bar")!;
  const stageEl = shell.querySelector<HTMLParagraphElement>("#warmup-stage")!;

  const done = (async () => {
    await loadPii(id, (p: PiiProgress) => {
      if (p.stage === "fetch_tokenizer") {
        stageEl.textContent = "Fetching tokenizer…";
      } else if (p.stage === "fetch_graph") {
        const pct = p.total > 0 ? Math.min(100, Math.round((p.loaded / p.total) * 100)) : 0;
        bar.style.width = pct + "%";
        stageEl.textContent = `Downloading model graph · ${pct}%`;
      } else if (p.stage === "fetch_weights") {
        const pct = p.total > 0 ? Math.min(100, Math.round((p.loaded / p.total) * 100)) : 0;
        bar.style.width = pct + "%";
        stageEl.textContent = `Downloading ${p.file} · ${pct}%`;
      } else if (p.stage === "compiling") {
        stageEl.textContent = "Compiling for the GPU…";
      } else if (p.stage === "ready") {
        bar.style.width = "100%";
        stageEl.textContent = "Ready";
      }
    });
  })();
  return { done };
}
